import { ICartEntry } from 'features/cart/types';

import { useStableCallback } from 'hooks/use-stable-callback';

import { findAppliedOfferMinimalCartEntry } from '../utils/applied-offer-ids';

interface UseEvictAppliedOfferArgs {
  /** Clears the legacy cart entry + Redux applied-offer record (`useCart`). */
  removeFromCart: (args: { cartId: string }) => void;
  /** Clears the matching minimal-cart discount/incentive entry (`useMinimalCart`). */
  removeItemFromMinimalCart: (entry: ICartEntry) => void;
  /** Current minimal-cart entries, searched for the matching offer row. */
  minimalCartEntries: readonly ICartEntry[];
}

/**
 * Builds the two-store applied-offer eviction sink used by the ON-gate
 * loyalty-service eviction (`useLoyaltyServiceAppliedOfferEviction`).
 *
 * It clears every cart representation of a delisted offer:
 * - the legacy cart entry / Redux applied-offer record (`removeFromCart`, keyed
 *   by `cartId`) — only when a `cartId` is provided. A minimal-cart-only offer
 *   (a favorited offer wrapper) has no `appliedOffers` record, so no `cartId` is
 *   passed and this step is skipped;
 * - the matching minimal-cart discount/incentive entry
 *   (`removeItemFromMinimalCart`, matched by `id` = `loyaltyEngineId` via its
 *   `referenceId`). This runs regardless of the minimal-cart UI experiment
 *   because the row is persisted whenever the unified menu is enabled (see
 *   `addDiscountOfferToMinimalCart`), so gating removal on the UI would orphan a
 *   backend-delisted offer that reappears when the experiment flag flips.
 *
 * Returns a stable reference so the eviction effect keys only on the settled
 * batch, not on cart churn.
 */
export function useEvictAppliedOffer({
  removeFromCart,
  removeItemFromMinimalCart,
  minimalCartEntries,
}: UseEvictAppliedOfferArgs): (args: { cartId?: string; id: string }) => void {
  return useStableCallback(({ cartId, id }: { cartId?: string; id: string }) => {
    if (cartId) {
      removeFromCart({ cartId });
    }
    const minimalEntry = findAppliedOfferMinimalCartEntry(minimalCartEntries, id);
    if (minimalEntry) {
      removeItemFromMinimalCart(minimalEntry);
    }
  });
}
